import type { List, Map } from 'immutable';
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import {
    getCreatedTime,
    getCreatorId,
    getModifiedBy,
    getModifiedTime,
} from '../../../../common/elements/utils/elementPropertyUtils';
import type { ImMNUser } from '../../../../common/users/userModelTypes';
import { getId } from '../../../../common/users/userHelper';
import { prop } from '../../../../common/utils/immutableHelper';
import { TIMES } from '../../../../common/utils/timeUtil';

export type ElementMetadataEntry = { userId: string; timestamp: number };
export type ElementMetadata = { created?: ElementMetadataEntry; modified?: ElementMetadataEntry };

export type ElementMetadataDisplayRow = {
    kind: 'created' | 'createdAndModified' | 'modified';
    user: ImMNUser;
    isCurrentUser: boolean;
    createdTimestamp?: number;
    modifiedTimestamp?: number;
};

/** Creates a metadata entry when both its author and timestamp are available. */
const createMetadataEntry = (userId: unknown, timestamp: number | undefined): ElementMetadataEntry | undefined => {
    if (!userId || timestamp === undefined) return undefined;
    return { userId: String(userId), timestamp };
};

/** Resolves metadata for single selections only; one set of timestamps can't describe a multi-selection. */
export const resolveElementMetadata = (selectedElements: List<ImMNElement>): ElementMetadata => {
    if (selectedElements.size !== 1) return {};

    const element = selectedElements.first();
    if (!element) return {};

    return {
        created: createMetadataEntry(getCreatorId(element), getCreatedTime(element)),
        modified: createMetadataEntry(getModifiedBy(element), getModifiedTime(element)),
    };
};

type BuildElementMetadataDisplayRowsArgs = {
    metadata?: ElementMetadata;
    users?: Map<string, ImMNUser>;
    currentUserId?: string;
    /** Formats a timestamp exactly as it will display, so equal-looking times can collapse. */
    formatTime: (timestamp: number) => string;
};

/** Builds one display row per resolved metadata author, combining same-author rows. */
export const buildElementMetadataDisplayRows = ({
    metadata,
    users,
    currentUserId,
    formatTime,
}: BuildElementMetadataDisplayRowsArgs): ElementMetadataDisplayRow[] => {
    const { created, modified } = metadata ?? {};
    const createdUser = created && (prop(created.userId, users) as ImMNUser | undefined);
    const modifiedUser = modified && (prop(modified.userId, users) as ImMNUser | undefined);

    const createdRow: ElementMetadataDisplayRow | undefined = created &&
        createdUser && {
            kind: 'created',
            user: createdUser,
            isCurrentUser: currentUserId === getId(createdUser),
            createdTimestamp: created.timestamp,
        };
    const modifiedRow: ElementMetadataDisplayRow | undefined = modified &&
        modifiedUser && {
            kind: 'modified',
            user: modifiedUser,
            isCurrentUser: currentUserId === getId(modifiedUser),
            modifiedTimestamp: modified.timestamp,
        };

    const sameUser = createdUser && modifiedUser && getId(createdUser) === getId(modifiedUser);
    if (!created || !modified || !createdRow || !sameUser) {
        return [createdRow, modifiedRow].filter((row): row is ElementMetadataDisplayRow => !!row);
    }

    // A same-user update reads as redundant when it happened soon after creation or when both
    // timestamps display identically (e.g. both "3 weeks ago"), so show creation only.
    const modifiedCloseToCreationTime =
        modified.timestamp - created.timestamp < 5 * TIMES.MINUTE ||
        formatTime(created.timestamp) === formatTime(modified.timestamp);

    if (modifiedCloseToCreationTime) return [createdRow];

    return [{ ...createdRow, kind: 'createdAndModified', modifiedTimestamp: modified.timestamp }];
};

/** Reports whether any metadata author resolves to a known user, i.e. whether rows can render. */
export const hasResolvableMetadataAuthor = ({
    metadata,
    users,
}: Pick<BuildElementMetadataDisplayRowsArgs, 'metadata' | 'users'>): boolean =>
    !!(metadata?.created && prop(metadata.created.userId, users)) ||
    !!(metadata?.modified && prop(metadata.modified.userId, users));
